import { defineMessages } from "react-intl";

import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import FieldType from "@mapmycustomers/shared/enum/fieldModel/FieldType";
import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import FrequencyStatus from "@mapmycustomers/shared/enum/frequency/FrequencyStatus";
import PlatformFilterOperator from "@mapmycustomers/shared/enum/PlatformFilterOperator";
import Cadenced from "@mapmycustomers/shared/types/base/Cadenced";
import { LegacyFilterCondition } from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";
import {
  Condition,
  PlatformFilterCondition,
} from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformFilterModel";
import { isDefined } from "@mapmycustomers/shared/util/assert";

import i18nService from "@app/config/I18nService";
import EmptyFilter from "@app/util/filters/EmptyFilter";
import FrequencyStatusFilter, {
  FREQUENCY_STATUS_FILTER_OPERATORS,
} from "@app/util/filters/FrequencyStatusFilter";
import FrequencyStatusFilterValue from "@app/util/filters/FrequencyStatusFilter/FrequencyStatusFilterValue";
import { isCombinedPlatformCondition, isLegacyCombinedCondition } from "@app/util/viewModel/assert";
import { getRegularFieldConditionValue } from "@app/util/viewModel/convertFromPlatformFilterModel";
import {
  filterOperatorToPlatformFilterOperatorMap,
  getRegularFieldPlatformConditionValue,
} from "@app/util/viewModel/convertToPlatformFilterModel";

import Field, { FieldProperties } from "./Field";

const upcomingSwapOperator = {
  [FilterOperator.EQUALS]: FilterOperator.EQUALS,
  [FilterOperator.GREATER_THAN]: FilterOperator.LESS_THAN,
  [FilterOperator.GREATER_THAN_OR_EQUAL]: FilterOperator.LESS_THAN_OR_EQUAL,
  [FilterOperator.LESS_THAN]: FilterOperator.GREATER_THAN,
  [FilterOperator.LESS_THAN_OR_EQUAL]: FilterOperator.GREATER_THAN_OR_EQUAL,
} as const;

export const messages = defineMessages({
  outSoon: {
    id: "frequencyStatusField.outSoon",
    defaultMessage: "Due in {days} {days, plural, one {day} other {days}}",
    description: "Frequency status field - out soon",
  },
  overdue: {
    id: "frequencyStatusField.overdue",
    defaultMessage: "{days} {days, plural, one {day} other {days}} past due",
    description: "Frequency status field - overdue",
  },
  overdueToday: {
    id: "frequencyStatusField.overdueToday",
    defaultMessage: "Due today",
    description: "Frequency status field - overdue Today",
  },
  pastDueDaysCount: {
    id: "frequencyIntervalField.pastDueDaysCount",
    defaultMessage: "{count} {count, plural, one {day} other {days}} past due",
    description: "Days count for Frequency Interval Field",
  },
  upToDate: {
    id: "frequencyStatusField.upToDate.dueDays",
    defaultMessage: "Up to date (due in {count} {count, plural, one {day} other {days}})",
    description: "Frequency status field - Up to date",
  },
});

class FrequencyStatusField extends Field {
  constructor(data: Omit<FieldProperties, "type">) {
    super({
      customFilterConfig: {
        defaultInstance: "frequencyStatus",
        filterInstances: { empty: EmptyFilter, frequencyStatus: FrequencyStatusFilter },
        operators: [
          ...FREQUENCY_STATUS_FILTER_OPERATORS,
          { instance: "empty", operator: FilterOperator.NOT_EMPTY },
          { instance: "empty", operator: FilterOperator.EMPTY },
        ],
      },
      customGridProperties: {
        cellRenderer: "frequencyCellRenderer",
      },
      exportColumnName: "cadenceStatus",
      features: [
        FieldFeature.FREQUENCY_STATUS_FIELD,
        FieldFeature.ENGAGEMENT,
        FieldFeature.FILTERABLE_ON_MAP,
        FieldFeature.FILTERABLE,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
        FieldFeature.SORTABLE,
      ],
      platformFilterNames: [data.name, "cadenceStatus"],
      type: FieldType.NUMBER,
      valueFormatter: (entity: unknown, value: unknown) => {
        const intl = i18nService.getIntl();
        if (!intl) {
          return String(value); // return value as is better than returning nothing
        }
        return (value as number) > 0
          ? intl.formatMessage(messages.overdue, { days: value as number })
          : intl.formatMessage(messages.outSoon, { days: -(value as number) });
      },
      ...data,
    });
  }

  getFormattedCsvValueFor(entity: unknown) {
    const status = (entity as Cadenced).cadenceStatus;
    const cadenceDaysOut = (entity as Cadenced).cadenceDaysOut ?? 0;
    return status === FrequencyStatus.UP_TO_DATE
      ? (i18nService.getIntl()?.formatMessage({
          id: "frequencyStatusField.upToDate",
          defaultMessage: "Up to date",
          description: "Up to date for Frequency status Field",
        }) ?? "Up to date")
      : status === FrequencyStatus.UPCOMING
        ? (i18nService.getIntl()?.formatMessage({
            id: "frequencyStatusField.upcoming",
            defaultMessage: "Upcoming",
            description: "Upcoming for Frequency status Field",
          }) ?? "Upcoming")
        : status === FrequencyStatus.PAST_DUE
          ? (i18nService
              .getIntl()
              ?.formatMessage(messages.pastDueDaysCount, { count: cadenceDaysOut }) ??
            `${cadenceDaysOut} ${cadenceDaysOut > 1 ? " days " : "day"} past due`)
          : "";
  }

  convertToPlatformCondition(filterCondition: LegacyFilterCondition): PlatformFilterCondition {
    if (isLegacyCombinedCondition(filterCondition)) {
      return {}; // combined condition is not supported
    } else {
      if (filterCondition.operator === FilterOperator.IN_ANY) {
        return {
          cadenceStatus: { [PlatformFilterOperator.GROUP_IN_ANY]: filterCondition.value },
        };
      } else if (filterCondition.operator === FilterOperator.IS_OVERDUE) {
        const subCondition = filterCondition.value as FrequencyStatusFilterValue | undefined;
        if (subCondition) {
          const operator = filterOperatorToPlatformFilterOperatorMap[subCondition.operator];
          return {
            [this.platformFilterNames[0]]:
              subCondition.operator === FilterOperator.IS_OVERDUE || !operator
                ? { [PlatformFilterOperator.GREATER_THAN_OR_EQUAL]: 0 }
                : { [operator]: subCondition.value },
          };
        }
        return {
          cadenceStatus: {
            [PlatformFilterOperator.CONTAINS]: [FrequencyStatus.PAST_DUE],
          },
        };
      } else if (filterCondition.operator === FilterOperator.WILL_BE_OVERDUE) {
        const subCondition = filterCondition.value as FrequencyStatusFilterValue;
        if (subCondition) {
          const finalOperator = subCondition.operator
            ? upcomingSwapOperator[subCondition.operator as keyof typeof upcomingSwapOperator]
            : undefined;
          const operator = finalOperator
            ? filterOperatorToPlatformFilterOperatorMap[finalOperator]
            : undefined;
          return {
            [this.platformFilterNames[0]]: {
              ...(operator
                ? { [operator]: isDefined(subCondition.value) ? -subCondition.value : undefined }
                : {}),
              ...(!operator ||
              ![
                FilterOperator.EQUALS,
                FilterOperator.LESS_THAN,
                FilterOperator.LESS_THAN_OR_EQUAL,
              ].includes(finalOperator!)
                ? { [PlatformFilterOperator.LESS_THAN]: 0 }
                : {}),
            },
          };
        }
        return {
          [this.platformFilterNames[0]]: {},
        };
      } else if (filterCondition.operator === FilterOperator.IS_UPCOMING) {
        return {
          cadenceStatus: filterCondition.value
            ? {
                [PlatformFilterOperator.CONTAINS]: [filterCondition.value],
              }
            : {
                [PlatformFilterOperator.CONTAINS]: [
                  FrequencyStatus.UPCOMING,
                  FrequencyStatus.UP_TO_DATE,
                ],
              },
        };
      } else if (
        filterCondition.operator === FilterOperator.EQUALS ||
        filterCondition.operator === FilterOperator.EMPTY
      ) {
        return {
          cadenceStatus: {
            [PlatformFilterOperator.EQUALS]: FrequencyStatus.UNKNOWN,
          },
        };
      } else if (
        filterCondition.operator === FilterOperator.NOT_EQUAL ||
        filterCondition.operator === FilterOperator.NOT_EMPTY
      ) {
        return {
          cadenceStatus: {
            [PlatformFilterOperator.NOT_EQUAL]: FrequencyStatus.UNKNOWN,
          },
        };
      }
    }
    return {
      [this.platformFilterNames[0]]: getRegularFieldPlatformConditionValue(this, filterCondition),
    };
  }

  convertFromPlatformCondition(
    filterCondition: PlatformFilterCondition
  ): LegacyFilterCondition | undefined {
    if (isCombinedPlatformCondition(filterCondition)) {
      return super.convertFromPlatformCondition(filterCondition);
    } else {
      if ("cadenceDaysOut" in filterCondition) {
        const cadenceDaysOut = filterCondition.cadenceDaysOut as Condition;
        if (PlatformFilterOperator.GREATER_THAN in cadenceDaysOut) {
          const value = cadenceDaysOut[PlatformFilterOperator.GREATER_THAN] as number;
          if (value < 0) {
            return {
              operator: FilterOperator.WILL_BE_OVERDUE,
              value: {
                operator: FilterOperator.LESS_THAN,
                value: -value,
              },
            };
          } else {
            return {
              operator: FilterOperator.IS_OVERDUE,
              value: {
                operator: value === 0 ? FilterOperator.IS_OVERDUE : FilterOperator.GREATER_THAN,
                value,
              },
            };
          }
        } else if (PlatformFilterOperator.GREATER_THAN_OR_EQUAL in cadenceDaysOut) {
          const value = cadenceDaysOut[PlatformFilterOperator.GREATER_THAN_OR_EQUAL] as number;
          if (value < 0) {
            return {
              operator: FilterOperator.WILL_BE_OVERDUE,
              value: {
                operator: FilterOperator.LESS_THAN_OR_EQUAL,
                value: -value,
              },
            };
          } else {
            return {
              operator: FilterOperator.IS_OVERDUE,
              value: {
                operator:
                  value === 0 ? FilterOperator.IS_OVERDUE : FilterOperator.GREATER_THAN_OR_EQUAL,
                value,
              },
            };
          }
        } else if (PlatformFilterOperator.LESS_THAN in cadenceDaysOut) {
          const value = cadenceDaysOut[PlatformFilterOperator.LESS_THAN] as number;
          if (value < 0) {
            return {
              operator: FilterOperator.WILL_BE_OVERDUE,
              value: {
                operator: FilterOperator.GREATER_THAN,
                value: -value,
              },
            };
          } else {
            return {
              operator: FilterOperator.IS_OVERDUE,
              value: {
                operator: value === 0 ? FilterOperator.IS_OVERDUE : FilterOperator.LESS_THAN,
                value,
              },
            };
          }
        } else if (PlatformFilterOperator.LESS_THAN_OR_EQUAL in cadenceDaysOut) {
          const value = cadenceDaysOut[PlatformFilterOperator.LESS_THAN_OR_EQUAL] as number;
          if (value < 0) {
            return {
              operator: FilterOperator.WILL_BE_OVERDUE,
              value: {
                operator: FilterOperator.GREATER_THAN_OR_EQUAL,
                value: -value,
              },
            };
          } else {
            return {
              operator: FilterOperator.IS_OVERDUE,
              value: {
                operator:
                  value === 0 ? FilterOperator.IS_OVERDUE : FilterOperator.LESS_THAN_OR_EQUAL,
                value,
              },
            };
          }
        } else if (PlatformFilterOperator.EQUALS in cadenceDaysOut) {
          const value = cadenceDaysOut[PlatformFilterOperator.EQUALS];
          if (value === null) {
            return {
              operator: FilterOperator.EMPTY,
              value: undefined,
            };
          } else {
            return {
              operator: FilterOperator.WILL_BE_OVERDUE,
              value: {
                operator: FilterOperator.EQUALS,
                value: -(value as number),
              },
            };
          }
        } else if (PlatformFilterOperator.NOT_EQUAL in cadenceDaysOut) {
          const value = cadenceDaysOut[PlatformFilterOperator.NOT_EQUAL];
          if (value === null) {
            return {
              operator: FilterOperator.NOT_EMPTY,
              value: undefined,
            };
          }
        }
      } else if ("cadenceStatus" in filterCondition) {
        // this block is only used for backwards compatibility with
        // existing SavedFilters
        if (filterCondition.cadenceStatus === FrequencyStatus.PAST_DUE) {
          return { operator: FilterOperator.IS_OVERDUE, value: undefined };
        }
        if (
          [FrequencyStatus.UP_TO_DATE, FrequencyStatus.UPCOMING].includes(
            filterCondition.cadenceStatus as FrequencyStatus
          )
        ) {
          return { operator: FilterOperator.IS_UPCOMING, value: filterCondition.cadenceStatus };
        }
        // end of compatibility block

        const cadenceStatus = filterCondition.cadenceStatus as Partial<
          Record<PlatformFilterOperator, FrequencyStatus | FrequencyStatus[]>
        >;

        if (PlatformFilterOperator.EQUALS in cadenceStatus) {
          const value = cadenceStatus[PlatformFilterOperator.EQUALS];
          if (value === FrequencyStatus.UNKNOWN) {
            return { operator: FilterOperator.EMPTY, value: FrequencyStatus.UNKNOWN };
          }
        } else if (PlatformFilterOperator.NOT_EQUAL in cadenceStatus) {
          const value = cadenceStatus[PlatformFilterOperator.NOT_EQUAL];
          if (value === FrequencyStatus.UNKNOWN) {
            return { operator: FilterOperator.NOT_EMPTY, value: FrequencyStatus.UNKNOWN };
          }
        } else if (
          PlatformFilterOperator.GROUP_IN_ANY in cadenceStatus &&
          Array.isArray(cadenceStatus[PlatformFilterOperator.GROUP_IN_ANY])
        ) {
          const value = cadenceStatus[PlatformFilterOperator.GROUP_IN_ANY];
          return { operator: FilterOperator.IN_ANY, value };
        }

        if (
          PlatformFilterOperator.CONTAINS in cadenceStatus &&
          Array.isArray(cadenceStatus[PlatformFilterOperator.CONTAINS]) &&
          cadenceStatus[PlatformFilterOperator.CONTAINS].length
        ) {
          const value = cadenceStatus[PlatformFilterOperator.CONTAINS];
          if (value.length === 1) {
            switch (value[0]) {
              case FrequencyStatus.PAST_DUE:
                return { operator: FilterOperator.IS_OVERDUE, value: undefined };
              case FrequencyStatus.UP_TO_DATE:
              case FrequencyStatus.UPCOMING:
                return { operator: FilterOperator.IS_UPCOMING, value: value[0] };
            }
          } else if (value.length === 2) {
            return { operator: FilterOperator.IS_UPCOMING, value: undefined };
          }
        }
      }
    }
    return getRegularFieldConditionValue(this, filterCondition);
  }
}

export default FrequencyStatusField;
